import React, { useEffect, useMemo, useState } from "react";
import { Bookmark, Loader2, X } from "lucide-react";

import { createSavedSearch } from "../../../services/savedSearchesApi";
import { markOnboarding } from "../../../onboarding/onboardingStore";
import { showErrorToast, showSuccessToast } from "../../../utils/toast";
import { US_STATES } from "./TenderFilters";

const STATE_NAMES = Object.fromEntries(US_STATES);

// A short, human list of what's about to be saved — the same information a
// person already sees as chips and rail sections, just restated in one place
// so saving isn't a leap of faith. Labels come from `facets` where the raw
// filter only holds a slug or code; a label list gets truncated rather than
// wrapping into a wall of text.
function describeFilters(filters, keywords, facets) {
  const chips = [];
  if (keywords?.trim()) chips.push(`"${keywords.trim()}"`);
  if (filters.status && filters.status !== "open") {
    chips.push(`Status: ${filters.status === "closed" ? "Closed" : "All"}`);
  }
  const joinLabelled = (values, labelOf, prefix) => {
    if (!values?.length) return;
    const names = values.map(labelOf).filter(Boolean);
    const shown = names.slice(0, 3).join(", ");
    const more = names.length > 3 ? ` +${names.length - 3} more` : "";
    chips.push(`${prefix}: ${shown}${more}`);
  };

  const typeLabels = Object.fromEntries((facets?.notice_types || []).map((t) => [t.value, t.label]));
  joinLabelled(filters.notice_type, (v) => typeLabels[v] || v, "Type");

  const categoryLabels = Object.fromEntries((facets?.categories || []).map((c) => [c.slug, c.label]));
  joinLabelled(filters.category, (v) => categoryLabels[v] || v, "Industry");

  joinLabelled(filters.state, (v) => STATE_NAMES[v] || v, "Location");
  joinLabelled(filters.buyer, (v) => v, "Organization");

  if (filters.set_aside_only) chips.push("Set-aside only");
  if (filters.posted_since_days) chips.push(`Posted in the last ${filters.posted_since_days} days`);
  if (filters.closing_within_days) chips.push(`Closing within ${filters.closing_within_days} days`);
  if (filters.has_documents) chips.push("Has documents");

  return chips;
}

export default function SaveSearchModal({
  open, onClose, filters, keywords, ordering, resultCount, facets, onSaved,
}) {
  const [name, setName] = useState("");
  const [alertOn, setAlertOn] = useState(false);
  const [frequency, setFrequency] = useState("daily");
  const [saving, setSaving] = useState(false);

  // Fresh every time it opens — reopening the dialog for a different search
  // must not still show the previous one's name or alert choice.
  useEffect(() => {
    if (open) {
      setName("");
      setAlertOn(false);
      setFrequency("daily");
    }
  }, [open]);

  // Guarded on `open`, not just the early `return null` below: this component
  // stays mounted the whole time the search page is, so without this an
  // Escape press anywhere on the page would call onClose even while closed.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  const chips = useMemo(
    () => describeFilters(filters, keywords, facets),
    [filters, keywords, facets]
  );

  if (!open) return null;

  const canSave = name.trim().length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const row = await createSavedSearch({
        name: name.trim(),
        state: { keywords: keywords || "", ordering, filters },
        alertEmail: alertOn,
        alertFrequency: alertOn ? frequency : "off",
      });
      showSuccessToast(`Saved "${row.name}"`);
      markOnboarding("tender");
      onSaved?.(row);
      onClose?.();
    } catch {
      showErrorToast("Could not save this search. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30 backdrop-blur-[1px]"
      onClick={() => !saving && onClose?.()}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-[540px] font-outfit border border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-5 flex items-start gap-3">
          <span className="w-[34px] h-[34px] rounded-[9px] bg-[#FDF6FC] grid place-items-center shrink-0">
            <Bookmark className="w-[17px] h-[17px] text-[#BB42AE]" />
          </span>
          <div className="flex-1">
            <h2 className="text-[18px] font-semibold text-[#18101C]">Save this search</h2>
            <p className="text-[13px] text-[#71717A] mt-0.5">
              It re-runs every time you open it, so the results stay current.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !saving && onClose?.()}
            className="text-[#9CA3AF] hover:text-[#52525B] shrink-0"
            aria-label="Close"
          >
            <X className="w-[18px] h-[18px]" />
          </button>
        </div>

        <div className="px-6 pt-4">
          <label htmlFor="saved-search-name" className="block text-[12px] font-semibold uppercase tracking-[0.06em] text-[#52525B] mb-1.5">
            Name
          </label>
          <input
            id="saved-search-name"
            type="text"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. AI & analytics, small business"
            className="w-full h-[38px] font-outfit text-[14px] px-3 border border-[#00000024]
                       rounded-md bg-white text-[#18101C] outline-none
                       focus:border-[#C95EBE] focus:ring-1 focus:ring-[#C95EBE]
                       placeholder:text-[#9CA3AF]"
          />

          <div className="mt-4 border border-[#E5E7EB] rounded-[10px] p-3.5 bg-[#FAFAFA]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#52525B]">
                What gets saved
              </span>
              <span className="text-[12px] text-[#71717A]">
                <b className="text-[#18101C] font-semibold">{resultCount?.toLocaleString?.() ?? resultCount}</b>{" "}
                result{resultCount === 1 ? "" : "s"} right now
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {chips.length === 0 ? (
                <span className="text-[12px] font-medium text-[#52525B] bg-white border border-[#E5E7EB] rounded-md px-2.5 py-1">
                  Every open tender — no filters set
                </span>
              ) : (
                chips.map((c) => (
                  <span
                    key={c}
                    className="text-[12px] font-medium text-[#52525B] bg-white border border-[#E5E7EB] rounded-md px-2.5 py-1"
                  >
                    {c}
                  </span>
                ))
              )}
            </div>
            <p className="mt-2.5 text-[11.5px] leading-[1.45] text-[#9CA3AF]">
              Sort order isn't saved — reopening always starts from Best match while a keyword is set.
            </p>
          </div>

          <div className="mt-3.5 border border-[#E5E7EB] rounded-[10px] p-3.5">
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <span className="relative flex shrink-0">
                <input
                  type="checkbox"
                  checked={alertOn}
                  onChange={() => setAlertOn((v) => !v)}
                  className="peer absolute inset-0 w-full h-full m-0 opacity-0 cursor-pointer"
                />
                <span
                  aria-hidden
                  className={`w-[18px] h-[18px] rounded-[4px] flex items-center justify-center peer-focus-visible:ring-2 peer-focus-visible:ring-[#8B55DD] ${
                    alertOn ? "bg-[#8B55DD]" : "border-[1.5px] border-[#B3B3B3]"
                  }`}
                >
                  {alertOn && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </span>
              </span>
              <span className="text-[14px] font-medium text-[#18101C]">
                Email me when new tenders match
              </span>
            </label>

            {alertOn && (
              <div className="flex gap-1.5 mt-3 pl-[27px]">
                {[["daily", "Daily"], ["weekly", "Weekly"]].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFrequency(value)}
                    className={`text-[13px] font-semibold rounded-md px-3.5 py-1.5 border transition-colors ${
                      frequency === value
                        ? "text-white bg-[#A04A97] border-[#A04A97]"
                        : "text-[#52525B] bg-white border-[#00000024] hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => !saving && onClose?.()}
            disabled={saving}
            className="px-4 py-2 rounded-md text-[14px] font-medium text-[#52525B] hover:bg-gray-100 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-[14px] font-semibold text-white
                       bg-[#A04A97] hover:bg-[#8f4188] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Saving...
              </>
            ) : (
              "Save search"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
